<template>
  <card dis-hover class="books-editor">
    <div class="card-header">
      <div class="card-header__title">
        <icon class="ms-help" size="16" type="md-book"></icon>
        <span class="ms-help card-header__title-text">{{
          activeBookName
        }}</span>
      </div>
    </div>
    <div class="books-editor__container">
      <div class="books-editor__controls">
        <i-input
          class="books-editor__filter"
          v-model="filterText"
          placeholder="Поиск по названию"
        />
        <i-button
          v-if="permission.create && !activeBookCreateDisabled"
          type="success"
          @click="editObject()"
        >
          Добавить
        </i-button>
      </div>
      <ul class="books-editor__list" v-if="filteredValues.length">
        <li
          class="books-editor__item ms-text"
          v-for="item in filteredValues"
          :key="item.id"
          @click="editObject(item)"
        >
          <div class="books-editor__title">
            {{ getLabel(item) }}
          </div>
          <div
            class="books-editor__action"
            v-if="permission.delete && !activeBookDeleteDisabled"
          >
            <icon
              class="ms-danger"
              size="14"
              type="md-trash"
              @click="deleteObject(item.id, $event)"
            />
          </div>
        </li>
      </ul>
      <div class="books-editor__nodata" v-else>
        <span class="ms-help">Объекты отсутствуют</span>
      </div>
    </div>
    <modal
      :class="`modal-window ${theme}`"
      @on-cancel="closeModal"
      v-model="editModal"
      width="480"
      :styles="{ top: '40px' }"
    >
      <p slot="header" style="color:#4484ce;text-align:center">
        <icon type="md-create" />
        <span style="margin-left: 8px;">
          {{ object.id ? "Редактирование" : "Создание" }} объекта
        </span>
      </p>
      <div class="books-editor__modal-content">
        <div
          class="books-editor__modal-row"
          v-for="attr in activeBookAttributes"
          :key="attr.value"
        >
          <template v-if="attr.type === 'string'">
            <span class="ms-help label">
              {{ getAttrLabel(attr) }}
            </span>
            <i-input
              size="default"
              v-model="object[attr.value]"
              :placeholder="attr.name"
              :disabled="attr.disabled && !!object.id"
            />
          </template>
          <template v-else-if="attr.type === 'phone'">
            <span class="ms-help label">
              {{ getAttrLabel(attr) }}
            </span>
            <i-input
              v-model="object[attr.value]"
              size="default"
              :placeholder="attr.name"
              @on-keydown="phoneHandler($event, attr.value)"
            />
          </template>
          <template v-else-if="attr.type === 'password'">
            <span class="ms-help label">
              {{ getAttrLabel(attr) }}
            </span>
            <i-input
              type="password"
              size="default"
              v-model="object[attr.value]"
              :placeholder="attr.name"
            />
          </template>
          <template v-else-if="attr.type === 'color'">
            <span class="ms-help label">
              {{ getAttrLabel(attr) }}
            </span>
            <color-picker
              v-if="object[attr.value]"
              v-model="object[attr.value]"
              recommend
              :transfer="false"
            />
          </template>
          <template v-else-if="attr.type === 'boolean'">
            <span class="ms-help label">
              {{ getAttrLabel(attr) }}
            </span>
            <i-switch v-model="object[attr.value]" />
          </template>
          <template v-else-if="attr.type === 'select' && sources[attr.source]">
            <span class="ms-help label">
              {{ getAttrLabel(attr) }}
            </span>
            <i-select
              :placeholder="`Выберите ${attr.name.toLowerCase()}`"
              v-model="object[attr.value]"
              :transfer="false"
              :multiple="attr.selectType === 'multiple'"
              size="default"
            >
              <i-option
                v-for="item in sources[attr.source]"
                :value="item.id"
                :key="item.id"
              >
                {{ item.name }}
              </i-option>
            </i-select>
          </template>
        </div>
      </div>
      <div slot="footer">
        <i-button
          :loading="loading"
          type="success"
          size="default"
          @click="saveObject"
          :disabled="!isSaveAvailable"
          long
        >
          Сохранить
        </i-button>
      </div>
    </modal>

    <modal :class="`modal-window ${theme}`" v-model="deleteModal" width="360">
      <p slot="header" style="color:#f60;text-align:center">
        <icon type="ios-information-circle" />
        <span style="margin-left: 4px">Удаление объекта</span>
      </p>
      <div class="ms-text" style="text-align:center">
        <p>Вы уверены, что хотите удалить данный объект?</p>
      </div>
      <div slot="footer">
        <i-button
          :loading="deleting"
          type="error"
          size="default"
          long
          @click="deleteHandler"
        >
          Удалить
        </i-button>
      </div>
    </modal>
  </card>
</template>

<script>
import { isMatching, checkDigitalInput } from '@/utils'

const role_statuses = [
  { id: 0, name: 'Администратор' },
  { id: 1, name: 'Статус №1 – УКИИ' },
  { id: 2, name: 'Статус №2 – Офис' },
  { id: 3, name: 'Статус №3 – Тендер' },
  { id: 4, name: 'Статус №4 – Поле' },
  { id: 5, name: 'Статус №5 – Просмотр' }
]

export default {
  props: [
    'active-book',
    'active-book-type',
    'active-book-name',
    'active-book-label',
    'active-book-attributes',
    'active-book-requests',
    'active-book-required',
    'active-book-create-disabled',
    'active-book-delete-disabled',
    'values',
    'permissions'
  ],
  data () {
    return {
      filterText: '',
      editModal: false,
      loading: false,
      deleting: false,
      object: {},
      deleteModal: false,
      deletingId: '',
      sources: {
        role_statuses
      }
    }
  },
  watch: {
    '$props.activeBookRequests': {
      handler: function (val) {
        if (!val) return

        val.forEach(source => this.getSource(source))
      },
      immediate: true
    }
  },
  computed: {
    theme () {
      return this.$store.state.theme
    },
    permission () {
      return this.permissions[this.activeBook] || {}
    },
    filteredValues () {
      return this.values
        .filter(v => {
          return this.activeBookLabel.some(name =>
            isMatching(v[name], this.filterText)
          )
        })
        .sort((a, b) => {
          const [prop] = this.activeBookLabel
          const aProp = a[prop] ? a[prop].toLowerCase() : ''
          const bProp = b[prop] ? b[prop].toLowerCase() : ''

          if (aProp > bProp) return 1
          if (aProp < bProp) return -1
          return 0
        })
    },
    isSaveAvailable () {
      return this.activeBookRequired.every(name => {
        if (name === 'role_status' && this.object[name] === 0) return true
        if (name === 'companies' && !this.object[name].length) return false

        return !!this.object[name]
      })
    }
  },
  methods: {
    getLabel (item) {
      return this.activeBookLabel.reduce(
        (prev, el) => `${prev} ${item[el]}`,
        ''
      )
    },
    getAttrLabel ({ name, value }) {
      return this.activeBookRequired.includes(value) ? `${name} *` : name
    },
    closeModal () {
      this.$set(this, 'object', {})
      this.editModal = false
    },
    editObject (item = {}) {
      if (!this.permission.update) return

      this.$set(this, 'object', {})
      this.activeBookAttributes.forEach(attr => {
        const { value, type } = attr
        let defaultValue

        if (!item[value]) {
          switch (type) {
            case 'color': {
              defaultValue = '#EA4CA3'
              break
            }
            case 'companies': {
              defaultValue = []
              break
            }
            default: {
              defaultValue = item[value]
            }
          }
        }

        this.$set(this.object, value, item[value] || defaultValue)
      })
      this.$set(this.object, 'id', item.id)
      this.editModal = true
    },
    async getSource ({ type, name }) {
      try {
        const url = `${type}/${name}`
        this.sources[name] = await this.$store.dispatch('GET_REQUEST', { url })
      } catch (error) {
        console.log(error)
      }
    },
    getData () {
      const data = { ...this.object }
      if (data.password === '' || data.password === null) delete data.password

      if (this.activeBook === 'own_company') {
        data.owner_company = true
      }

      return data
    },
    async saveObject () {
      try {
        this.loading = true

        const data = this.getData()
        await this.$store.dispatch('POST_REQUEST', {
          url: `${this.activeBookType}/${this.activeBook}`,
          data
        })

        this.$emit('edit-handler')
        this.closeModal()
        this.$Message.success('Сохранение объекта выполнено успешно')
        this.loading = false
      } catch (error) {
        console.log(error)
        const message =
          error.response.data[0] || 'Произошла ошибка при сохранении объекта'
        this.$Message.error(message)
        this.loading = false
      }
    },
    async deleteObject (id, e) {
      e.stopPropagation()
      this.deletingId = id
      this.deleteModal = true
    },
    async deleteHandler () {
      try {
        this.deleting = true
        await this.$store.dispatch('DELETE_REQUEST', {
          url: `${this.activeBookType}/${this.activeBook}?id=${this.deletingId}`
        })
        this.$emit('edit-handler')
        this.closeModal()
        this.$Message.success('Удаление объекта выполнено успешно')
        this.deleting = false
      } catch (error) {
        console.log(error)
        this.deleting = false
        this.$Message.error('Произошла ошибка при удалении объекта')
      }
    },
    phoneHandler (e, key) {
      if (checkDigitalInput(e)) {
        e.preventDefault()
      }
    }
  }
}
</script>

<style lang="less">
.books-editor {
  height: calc(100vh - 112px);

  &__list {
    height: calc(100vh - 204px);
    overflow: auto;
  }

  &__controls {
    display: flex;
    margin-bottom: 16px;

    .ivu-input {
      width: 75%;
    }

    .ivu-btn {
      margin-left: 16px;
    }
  }

  &__list {
    font-size: 12px;
    list-style-type: none;
    border: 1px solid #dcdee2;
    border-radius: 4px;
    padding: 4px 0;
  }

  &__nodata {
    padding: 16px;
    text-align: center;
  }

  &__item {
    display: flex;
    justify-content: space-between;
    padding: 4px 8px;
    cursor: pointer;

    &:hover {
      background-color: rgba(@primary-color, 0.05);

      .books-editor__action {
        display: block;
      }
    }
  }

  &__action {
    display: none;

    &:hover {
      opacity: 0.8;
    }
  }

  &__modal {
    &-row {
      &:not(:first-child) {
        margin-top: 12px;
      }

      .ivu-input-number {
        width: 100%;

        &-handler-wrap {
          display: none;
        }
      }
    }

    &-color {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
  }
}
</style>
